// src/utils/storageUrl.js
// We migrated Supabase projects. Older records store absolute image URLs that
// point at the previous project's host (now dead → DNS errors). Rewrite any
// *.supabase.co URL to the CURRENT project's origin, preserving the path.
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || '';

let currentOrigin = '';
try {
  currentOrigin = SUPABASE_URL ? new URL(SUPABASE_URL).origin : '';
} catch (e) {
  currentOrigin = '';
}

// Matches https://<ref>.supabase.co[/path...]
const SUPABASE_HOST_RE = /^https:\/\/[a-z0-9-]+\.supabase\.co(\/.*)?$/i;

/**
 * Normalize a Supabase storage URL to the current project's host.
 * Non-supabase URLs (or when no current origin is configured) are returned
 * unchanged. Idempotent for URLs already on the current host.
 */
export const resolveStorageUrl = (url) => {
  if (!url || typeof url !== 'string' || !currentOrigin) return url;
  const match = url.match(SUPABASE_HOST_RE);
  if (!match) return url;
  const path = match[1] || '';
  return `${currentOrigin}${path}`;
};

/**
 * Global safety net: any <img> that fails to load (e.g. old-project host that
 * no longer resolves) gets its host rewritten to the current project and
 * retried once. Covers direct <img src> usages that don't go through the
 * optimized-image hook. Call once at app startup.
 */
export const installStorageUrlFallback = () => {
  if (typeof window === 'undefined' || !currentOrigin) return;
  window.addEventListener(
    'error',
    (event) => {
      const el = event.target;
      if (!el || el.tagName !== 'IMG' || el.dataset.storageRetried) return;
      const original = el.currentSrc || el.src;
      const fixed = resolveStorageUrl(original);
      if (fixed && fixed !== original) {
        el.dataset.storageRetried = '1';
        el.src = fixed;
      }
    },
    true // capture phase — resource error events don't bubble
  );
};

export default resolveStorageUrl;
